import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { alpha } from "@mui/material/styles";

import ProductIconTile from "../ProductIconTile";

function mergeSx(...items) {
  return items.flatMap((item) => (Array.isArray(item) ? item : [item])).filter(Boolean);
}

function ProductDialogHeader({
  closeAriaLabel,
  description,
  headerActions,
  icon,
  iconNode,
  iconTone,
  onClose,
  showCloseButton,
  title,
}) {
  if (!title && !description && !icon && !iconNode && !showCloseButton && !headerActions) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        alignItems: "center",
        bgcolor: theme.palette.surface.muted,
        borderBottom: `1px solid ${theme.palette.border.default}`,
        color: theme.palette.text.primary,
        display: "flex",
        gap: { xs: 1.25, sm: 2 },
        justifyContent: "space-between",
        minHeight: 68,
        px: { xs: 2, sm: 3 },
        py: 1.75,
      })}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: 1.5, minWidth: 0 }}>
        {iconNode || (icon ? <ProductIconTile icon={icon} tone={iconTone} /> : null)}
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography
              sx={{
                color: "text.primary",
                fontSize: { xs: 17, sm: 18 },
                fontWeight: 700,
                lineHeight: { xs: "22px", sm: "24px" },
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography
              sx={{
                color: "text.secondary",
                display: "-webkit-box",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: "18px",
                mt: title ? 0.25 : 0,
                overflow: "hidden",
                overflowWrap: "anywhere",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {headerActions || showCloseButton ? (
        <Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, gap: 1 }}>
          {headerActions}
          {showCloseButton ? (
            <IconButton
              aria-label={closeAriaLabel}
              onClick={onClose}
              sx={{
                color: "text.secondary",
                height: 36,
                width: 36,
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default function ProductDialog({
  actions,
  actionsSx,
  children,
  closeAriaLabel = "Fechar",
  contentSx,
  description,
  headerActions,
  icon,
  iconNode,
  iconTone = "violet",
  onClose,
  open,
  PaperProps,
  paperSx,
  rootProps,
  showCloseButton = true,
  title,
  TransitionProps,
  width = { xs: "calc(100% - 24px)", sm: 560 },
  ...dialogProps
}) {
  const { sx: rootSx, ...rootRest } = rootProps || {};
  const { sx: paperPropsSx, ...paperPropsRest } = PaperProps || {};

  return (
    <Dialog
      {...dialogProps}
      open={open}
      onClose={onClose}
      maxWidth={false}
      TransitionProps={TransitionProps}
      PaperProps={{
        ...paperPropsRest,
        sx: mergeSx(
          (theme) => ({
            bgcolor: theme.palette.background.paper,
            borderRadius: "16px",
            boxShadow:
              theme.palette.mode === "dark"
                ? "none"
                : `0 18px 42px ${alpha(theme.palette.common.black, 0.18)}`,
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 32px)",
            overflow: "hidden",
            width,
          }),
          paperSx,
          paperPropsSx,
        ),
      }}
    >
      <Box
        {...rootRest}
        sx={mergeSx(
          {
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            maxHeight: "calc(100vh - 32px)",
            minHeight: 0,
          },
          rootSx,
        )}
      >
        <ProductDialogHeader
          closeAriaLabel={closeAriaLabel}
          description={description}
          headerActions={headerActions}
          icon={icon}
          iconNode={iconNode}
          iconTone={iconTone}
          onClose={onClose}
          showCloseButton={showCloseButton}
          title={title}
        />
        <DialogContent
          sx={mergeSx(
            {
              flex: "1 1 auto",
              minHeight: 0,
              overflowX: "hidden",
              overflowY: "auto",
              p: { xs: 2, sm: 3 },
            },
            contentSx,
          )}
        >
          {children}
        </DialogContent>
        {actions ? (
          <Box
            sx={mergeSx(
              (theme) => ({
                alignItems: "center",
                borderTop: `1px solid ${theme.palette.border.default}`,
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                flexShrink: 0,
                gap: 1,
                justifyContent: "flex-end",
                px: 3,
                py: 2,
              }),
              actionsSx,
            )}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
}
